import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Copy,
  Check,
  RefreshCw,
  ArrowRight,
  Wand2,
} from "lucide-react";
import PageHero from "../../components/PageHero";
import { useToast } from "../../hooks/use-toast";
import {
  SCRIPT_BRANCHEN,
  SCRIPT_PLATFORMS,
  SCRIPT_GOALS,
} from "../../data/mock";

// Template-based intelligent script generator (no external AI)
const HOOKS_BY_GOAL = {
  "Mehr Anfragen / Buchungen": [
    "Stop scrollen – wenn du {branche_kontext}, brauchst du das hier.",
    "3 Gründe, warum 90% von {branche_zielgruppe} bei uns landen.",
    "Was niemand dir über {branche_kurz} sagt:",
    "Diese 1 Sache hat unsere Anfragen verdreifacht.",
  ],
  "Bekanntheit aufbauen": [
    "Das ist {info1} – und das musst du wissen.",
    "Warum alle gerade über {branche_kurz} reden.",
    "3 Dinge, die du über uns wissen solltest:",
    "POV: Du entdeckst gerade die beste {branche_kurz} in deiner Stadt.",
  ],
  "Produkt / Angebot vorstellen": [
    "Das hier verändert alles, wenn du {branche_kontext}.",
    "Ich zeig dir {info1} in 30 Sekunden.",
    "Wenn du das siehst, willst du es haben.",
    "Die 3 Features, die {info1} besonders machen:",
  ],
  "Vertrauen aufbauen": [
    "Hier ist die ehrliche Wahrheit über {branche_kurz}.",
    "3 Dinge, die ich als {branche_kurz}-Profi nie machen würde.",
    "Bevor du jemandem dein Geld gibst – sieh dir das an.",
    "Was wir anders machen als alle anderen in {branche_kurz}.",
  ],
  "Verkauf direkt (Hard Sell)": [
    "Letzter Tag – danach ist Schluss.",
    "Wenn du {branche_kontext}, ist das hier für dich.",
    "Nur heute: {info1} mit {info2}.",
    "Ich höre auf, dir das zu erklären – entweder du willst es oder nicht.",
  ],
};

const BODIES = [
  "Hier ist der Kontext: {info1}. Und das macht den Unterschied: {info2}.",
  "Schritt 1: {info1}. Schritt 2: {info2}. Schritt 3: Das Ergebnis spricht für sich.",
  "Was die meisten {branche_kurz}-Anbieter falsch machen? Sie ignorieren {info2}. Wir nicht.",
  "Schau – {info1} ist der Standard. Aber {info2} ist das, was wirklich zählt.",
];

const CTAS_BY_GOAL = {
  "Mehr Anfragen / Buchungen": [
    "Link in Bio – schreib uns für ein kostenloses Erstgespräch.",
    "Kommentier „INFO“ und wir melden uns bei dir.",
    "DM uns „START“ – wir klären den Rest.",
  ],
  "Bekanntheit aufbauen": [
    "Folge für mehr {branche_kurz}-Tipps.",
    "Speicher das Video für später – du wirst es brauchen.",
    "Tag jemanden, der das sehen muss.",
  ],
  "Produkt / Angebot vorstellen": [
    "Link in Bio – sieh dir alle Details an.",
    "Schreib uns „INFO“ für alle Details.",
    "Swipe up und sieh dir {info1} live an.",
  ],
  "Vertrauen aufbauen": [
    "Folge für mehr ehrliche Insights aus {branche_kurz}.",
    "Speicher das, falls du selbst mal {branche_kontext}.",
    "Kommentier deine Frage – ich beantworte alle.",
  ],
  "Verkauf direkt (Hard Sell)": [
    "Link in Bio. Heute. Sonst weg.",
    "DM „GO“ – wir starten sofort.",
    "Letzte Chance – Link in Bio.",
  ],
};

const BRANCHE_CONTEXT = {
  "Restaurant / Gastronomie": {
    kontext: "ein Restaurant betreibst",
    zielgruppe: "Gastronomen",
    kurz: "Gastro",
  },
  "Eventlocation / Club": {
    kontext: "einen Club oder eine Eventlocation hast",
    zielgruppe: "Locations",
    kurz: "Eventlocation",
  },
  "E-Commerce / Online-Shop": {
    kontext: "einen Online-Shop führst",
    zielgruppe: "Shop-Owner",
    kurz: "E-Commerce",
  },
  "Dienstleister B2B": {
    kontext: "B2B-Dienstleistungen anbietest",
    zielgruppe: "Dienstleister",
    kurz: "B2B-Service",
  },
  "Personal Brand / Coach": {
    kontext: "als Coach oder Personal Brand sichtbar werden willst",
    zielgruppe: "Coaches",
    kurz: "Coaching",
  },
  "Fitnessstudio / Sport": {
    kontext: "ein Fitnessstudio betreibst",
    zielgruppe: "Studios",
    kurz: "Fitness",
  },
  "Beauty / Friseur / Kosmetik": {
    kontext: "im Beauty-Bereich arbeitest",
    zielgruppe: "Beauty-Pros",
    kurz: "Beauty",
  },
  Immobilien: {
    kontext: "Immobilien verkaufst oder vermittelst",
    zielgruppe: "Makler",
    kurz: "Immobilien",
  },
  Handwerk: {
    kontext: "einen Handwerksbetrieb hast",
    zielgruppe: "Handwerker",
    kurz: "Handwerk",
  },
  "Auto / Mobilität": {
    kontext: "in der Auto-Branche tätig bist",
    zielgruppe: "Auto-Händler",
    kurz: "Auto",
  },
  "Mode / Fashion": {
    kontext: "eine Modemarke aufbaust",
    zielgruppe: "Brands",
    kurz: "Fashion",
  },
  Sonstiges: {
    kontext: "deine Marke aufbauen willst",
    zielgruppe: "Unternehmen",
    kurz: "deiner Branche",
  },
};

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const fill = (template, vars) =>
  template
    .replace(/{branche_kontext}/g, vars.kontext)
    .replace(/{branche_zielgruppe}/g, vars.zielgruppe)
    .replace(/{branche_kurz}/g, vars.kurz)
    .replace(/{info1}/g, vars.info1 || vars.kurz)
    .replace(/{info2}/g, vars.info2 || "unsere Erfahrung");

const generateScript = ({ branche, info1, info2, platform, goal }) => {
  const ctx = BRANCHE_CONTEXT[branche] || BRANCHE_CONTEXT["Sonstiges"];
  const vars = {
    ...ctx,
    info1: info1.trim() || ctx.kurz,
    info2: info2.trim() || "die strategische Umsetzung",
  };
  const hook = fill(pick(HOOKS_BY_GOAL[goal]), vars);
  const reHook = fill(
    "Bleib dran – das Ende erklärt, warum das auch für dich relevant ist.",
    vars
  );
  const body = fill(pick(BODIES), vars);
  const valueLine = fill(
    "Wenn du {branche_kontext}, dann macht genau das den Unterschied zwischen Reichweite und echten Anfragen.",
    vars
  );
  const cta = fill(pick(CTAS_BY_GOAL[goal]), vars);

  const seconds = platform === "YouTube Shorts" ? "45" : platform === "Instagram Reel" ? "30" : "22";

  return `🎬 ${platform} Skript – ${branche}
Ziel: ${goal}
Länge: ~${seconds} Sekunden

[0–3s] HOOK
${hook}

[3–6s] RE-HOOK
${reHook}

[6–18s] BODY / VALUE
${body}

${valueLine}

[18–22s] CTA
${cta}

—
✍️ Tipps für die Umsetzung:
• Schneller Schnitt alle 1–2 Sekunden
• Untertitel einblenden (80% schauen ohne Ton)
• Hook visuell unterstützen – nicht nur sagen
• Erste 3 Sekunden entscheiden alles`;
};

const VideoSkriptPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    branche: SCRIPT_BRANCHEN[0],
    info1: "",
    info2: "",
    platform: SCRIPT_PLATFORMS[0],
    goal: SCRIPT_GOALS[0],
  });
  const [script, setScript] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGenerate = (e) => {
    e?.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setScript(generateScript(form));
      setLoading(false);
      toast({
        title: "Skript generiert ✓",
        description: "Du kannst es jetzt kopieren und nutzen.",
      });
    }, 700);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(script);
      setCopied(true);
      toast({ title: "In Zwischenablage kopiert ✓" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Konnte nicht kopieren" });
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Kostenloses Tool"
        title="KI Video-Skript"
        gradient="Ersteller."
        subtitle="In 30 Sekunden zum fertigen Skript für TikTok, Instagram Reel oder YouTube Shorts. Branche eingeben, 1–2 Infos rein – fertig."
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Left: Form */}
            <div className="lg:col-span-5">
              <div className="sticky top-28 p-7 md:p-8 rounded-3xl bg-neutral-50 border border-black/5">
                <div className="flex items-center gap-2 mb-6">
                  <Wand2 size={18} className="text-pink-500" />
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-700">
                    Skript-Generator
                  </span>
                </div>

                <form onSubmit={handleGenerate} className="space-y-5">
                  <div>
                    <label className="text-xs font-bold text-neutral-700 uppercase tracking-wider">
                      Branche
                    </label>
                    <select
                      value={form.branche}
                      onChange={(e) =>
                        setForm({ ...form, branche: e.target.value })
                      }
                      className="mt-2 w-full px-4 py-3.5 rounded-2xl bg-white border border-black/10 focus:border-pink-400 outline-none font-medium text-neutral-900"
                    >
                      {SCRIPT_BRANCHEN.map((b) => (
                        <option key={b}>{b}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-neutral-700 uppercase tracking-wider">
                      Was bietest du an? (1–2 Wörter)
                    </label>
                    <input
                      value={form.info1}
                      onChange={(e) =>
                        setForm({ ...form, info1: e.target.value })
                      }
                      placeholder="z.B. Premium Burger, Personal Training"
                      className="mt-2 w-full px-4 py-3.5 rounded-2xl bg-white border border-black/10 focus:border-pink-400 outline-none font-medium text-neutral-900 placeholder:text-neutral-400"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-neutral-700 uppercase tracking-wider">
                      Was macht dich besonders?
                    </label>
                    <input
                      value={form.info2}
                      onChange={(e) =>
                        setForm({ ...form, info2: e.target.value })
                      }
                      placeholder="z.B. handgemacht, 24/7 Support"
                      className="mt-2 w-full px-4 py-3.5 rounded-2xl bg-white border border-black/10 focus:border-pink-400 outline-none font-medium text-neutral-900 placeholder:text-neutral-400"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-bold text-neutral-700 uppercase tracking-wider">
                        Plattform
                      </label>
                      <select
                        value={form.platform}
                        onChange={(e) =>
                          setForm({ ...form, platform: e.target.value })
                        }
                        className="mt-2 w-full px-3 py-3.5 rounded-2xl bg-white border border-black/10 focus:border-pink-400 outline-none font-medium text-neutral-900"
                      >
                        {SCRIPT_PLATFORMS.map((p) => (
                          <option key={p}>{p}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-neutral-700 uppercase tracking-wider">
                        Ziel
                      </label>
                      <select
                        value={form.goal}
                        onChange={(e) =>
                          setForm({ ...form, goal: e.target.value })
                        }
                        className="mt-2 w-full px-3 py-3.5 rounded-2xl bg-white border border-black/10 focus:border-pink-400 outline-none font-medium text-neutral-900"
                      >
                        {SCRIPT_GOALS.map((g) => (
                          <option key={g}>{g}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full mt-2 inline-flex items-center justify-center gap-3 px-7 py-5 rounded-full bg-neutral-950 text-white text-sm font-bold tracking-[0.12em] uppercase hover:bg-neutral-800 transition-all duration-300 disabled:opacity-60 group"
                  >
                    {loading ? (
                      <>
                        <RefreshCw size={18} className="animate-spin" />
                        Generiere…
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} />
                        Skript generieren
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Right: Output */}
            <div className="lg:col-span-7">
              <div className="min-h-[500px] p-7 md:p-8 rounded-3xl bg-neutral-950 text-white relative overflow-hidden">
                <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-[radial-gradient(circle,rgba(236,72,153,0.18),transparent_70%)]" />

                <div className="relative flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-300">
                      Dein Skript
                    </span>
                  </div>
                  {script && (
                    <button
                      onClick={handleCopy}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-xs font-semibold transition-colors"
                    >
                      {copied ? (
                        <>
                          <Check size={14} className="text-green-400" /> Kopiert
                        </>
                      ) : (
                        <>
                          <Copy size={14} /> Kopieren
                        </>
                      )}
                    </button>
                  )}
                </div>

                {!script ? (
                  <div className="relative h-[420px] flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 via-orange-500 to-purple-500 flex items-center justify-center mb-5">
                      <Sparkles size={26} className="text-white" />
                    </div>
                    <h3 className="font-display font-extrabold text-2xl tracking-tight">
                      Bereit für dein Skript?
                    </h3>
                    <p className="mt-2 text-neutral-400 max-w-sm">
                      Füll links die Felder aus und klick auf „Skript
                      generieren“. Dein fertiges Skript erscheint hier.
                    </p>
                  </div>
                ) : (
                  <pre className="relative whitespace-pre-wrap font-mono text-[13.5px] md:text-[14px] leading-[1.7] text-neutral-100 max-h-[600px] overflow-auto">
                    {script}
                  </pre>
                )}
              </div>

              <div className="mt-5 p-5 rounded-2xl bg-amber-50 border border-amber-200/60 text-sm text-amber-900">
                <span className="font-bold">Hinweis:</span> Dieses Skript ist
                ein Startpunkt. Für maximale Performance: kombiniert es mit
                eurer Brand-Stimme, testet 2–3 Hook-Varianten und passt das
                Tempo an.{" "}
                <Link
                  to="/kontakt"
                  className="underline font-semibold hover:text-pink-600"
                >
                  Wir helfen euch bei der Umsetzung →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-neutral-50">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-[-0.03em] text-neutral-950">
            Skript ist nur der Anfang.{" "}
            <span className="bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 bg-clip-text text-transparent">
              Wir produzieren es für dich.
            </span>
          </h2>
          <p className="mt-4 text-lg text-neutral-600">
            Konzept, Dreh, Schnitt, Posting – wir übernehmen den ganzen
            Prozess.
          </p>
          <Link
            to="/kontakt"
            className="mt-8 inline-flex items-center gap-3 px-8 py-5 rounded-full bg-neutral-950 text-white text-sm font-bold tracking-[0.12em] uppercase hover:bg-neutral-800 transition-all duration-300 group"
          >
            Content produzieren lassen
            <ArrowRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>
        </div>
      </section>
    </>
  );
};

export default VideoSkriptPage;
